// Cross-Phase Continuity Banner — renders on Phase 2/3/4 results
// pages when a same-session Phase 1 result exists for the same
// genotype-CSV SHA-256.
//
// Visual:
//   - Green left-border (4px) for all-clean state.
//   - Amber left-border (4px) for warning state.
//   - Subtle gray bg (#F8F9FA) so it doesn't compete with content.
//   - Click → expand inline (heritability table + warnings).
//   - Second click → collapse. Persistent dismiss (×) per-session.
//
// Niranjan, Feb 2026 — BATCH-NEXT-1.

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, ChevronDown, X, ExternalLink } from 'lucide-react';
import {
  getAnalysisRecord,
  findPhase1ByGenotypeHash,
} from '@/utils/molecularSessionRegistry';

const DISMISS_KEY = 'bkagrid:cross_phase_banner_dismissed_v1';


function readDismissedSet() {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')); }
  catch { return new Set(); }
}


function writeDismissedSet(set) {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); }
  catch { /* noop */ }
}


export function CrossPhaseContinuityBanner({ currentAnalysisId, currentPhase }) {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Re-resolve on every analysisId change (covers in-page navigation)
  const phase1 = useMemo(() => {
    const cur = getAnalysisRecord(currentAnalysisId);
    if (!cur?.genotypeHash) return null;
    return findPhase1ByGenotypeHash(cur.genotypeHash);
  }, [currentAnalysisId]);

  useEffect(() => {
    if (!currentAnalysisId) return;
    setDismissed(readDismissedSet().has(currentAnalysisId));
  }, [currentAnalysisId]);

  if (!phase1?.summary || dismissed) return null;
  const s = phase1.summary;
  const isClean = s.cleanState;
  const accent = isClean ? '#16A34A' : '#D97706';   // green-600 / amber-600
  const accentSoft = isClean ? '#DCFCE7' : '#FEF3C7';
  const Icon = isClean ? CheckCircle2 : AlertTriangle;

  const traitSummary = Object.entries(s.traits || {})
    .map(([t, v]) => `${v.band} for ${t}`)
    .join(', ');

  const headline = isClean
    ? `Phase 1 QC complete — ${s.nRetained}/${s.nInput} markers, H² ${traitSummary || '(no traits scored)'}. No re-QC needed for downstream phases.`
    : `Phase 1 QC complete with caveats — ${s.nRetained}/${s.nInput} markers; ${(s.warnings || []).join('; ')}. Review Phase 1 before deploying Phase ${currentPhase} results.`;

  const onDismiss = (e) => {
    e.stopPropagation();
    const set = readDismissedSet();
    set.add(currentAnalysisId);
    writeDismissedSet(set);
    setDismissed(true);
  };

  return (
    <div
      className="rounded-lg cursor-pointer transition-colors"
      style={{
        background: '#F8F9FA',
        borderLeft: `4px solid ${accent}`,
        border: `1px solid ${accent}25`,
      }}
      onClick={() => setExpanded(v => !v)}
      data-testid="cross-phase-banner"
      data-clean={isClean ? 'true' : 'false'}
    >
      <div className="px-4 py-2.5 flex items-center gap-3">
        <div className="flex items-center justify-center rounded-full shrink-0"
          style={{ width: 26, height: 26, background: accentSoft, color: accent }}>
          <Icon size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-slate-700 leading-snug truncate">
            {headline}
          </div>
        </div>
        {phase1.analysisId && (
          <a href={`/results/${phase1.analysisId}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 shrink-0"
            style={{ color: accent }}
            onClick={e => e.stopPropagation()}
            data-testid="cross-phase-banner-link">
            <ExternalLink size={11} /> Open Phase 1
          </a>
        )}
        <ChevronDown size={14}
          className={`shrink-0 transition-transform text-slate-400 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true" />
        <button onClick={onDismiss}
          className="ml-1 p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 shrink-0"
          aria-label="Dismiss banner"
          data-testid="cross-phase-banner-dismiss">
          <X size={12} />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-slate-200/60" data-testid="cross-phase-banner-expanded">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="font-semibold text-slate-700 mb-1">Heritability per trait</div>
              <table className="w-full">
                <tbody>
                  {Object.entries(s.traits || {}).map(([trait, v]) => (
                    <tr key={trait} className="border-b border-slate-100 last:border-0">
                      <td className="py-1 text-slate-600">{trait}</td>
                      <td className="py-1 text-right font-mono tabular-nums">
                        H² = {typeof v.h2 === 'number' ? v.h2.toFixed(3) : '—'}
                      </td>
                      <td className="py-1 text-right">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{
                            background: v.band === 'high' ? '#DCFCE7'
                                       : v.band === 'moderate' ? '#FEF3C7' : '#FEE2E2',
                            color: v.band === 'high' ? '#166534'
                                   : v.band === 'moderate' ? '#92400E' : '#991B1B',
                          }}>
                          {v.band}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="font-semibold text-slate-700 mb-1">Warnings from Phase 1</div>
              {(s.warnings && s.warnings.length > 0) ? (
                <ul className="space-y-1 text-slate-600">
                  {s.warnings.map((w, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <AlertTriangle size={11} className="text-amber-600 mt-0.5 shrink-0" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-slate-500 italic">No warnings — Phase 1 ran clean.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
